// 作品與展覽主頁（/work/）：把 showcase.json 的每一筆（work／exhibition／extra 參照）解析成畫面要的資料。
// 純函式、沒有 Vue 依賴：頁面、測試（scripts/test-showcase.mjs）與 route manifest 共用同一套規則。
import showcase from '@/data/showcase.json'
import catalog from '@/data/work-catalog.json'
import exhibitions from '@/data/exhibitions.json'
import { resolveMedia } from '@/components/work/record-utils'

export const workBySlug = Object.fromEntries(catalog.map(w => [w.slug, w]))
export const exhibitionBySlug = Object.fromEntries(exhibitions.map(e => [e.slug, e]))
export const threads = showcase.threads
export const threadById = Object.fromEntries(showcase.threads.map(t => [t.id, t]))

const pick = (zh, a, b) => (zh ? a || b : b || a) || ''
// 展覽的連結：手動收錄、直接用作品頁的那幾筆（The Last Input）link 以 /exhibition/ 開頭
export const exhibitionPath = x => (x?.link && String(x.link).startsWith('/exhibition/') ? x.link.replace(/\/?$/, '/') : `/exhibitions/${x.slug}/`)
const ymd = d => (d ? d.replace(/-/g, '.') : '')
export function dateRange(dates) {
  if (!dates?.start) return ''
  const s = ymd(dates.start)
  if (dates.ongoing) return `${s} –`
  if (!dates.end || dates.end === dates.start) return s
  const e = ymd(dates.end)
  return `${s} – ${e.slice(0, 4) === s.slice(0, 4) ? e.slice(5) : e}`
}

export function resolveItem(entry, zh) {
  const w = entry.work ? workBySlug[entry.work] : null
  const x = entry.exhibition ? exhibitionBySlug[entry.exhibition] : null
  const extra = entry.extra ? showcase.extra?.[entry.extra] : null
  const asWork = w && !entry.workAsLink
  // extra 裡 kind=exhibition 的紀錄（沒有獨立展覽頁的國際展出、駐村）跟 exhibitions.json 的展覽一樣看待
  const xe = !x && extra?.kind === 'exhibition' ? extra : null
  const combined = asWork && (x || xe)
  const kind = pick(zh, entry.kindZh, entry.kindEn) || (combined ? (zh ? '作品＋展覽' : 'Work + exhibition') : asWork ? (zh ? '作品' : 'Work') : x ? (zh ? '展覽' : 'Exhibition') : pick(zh, extra?.kindZh, extra?.kindEn) || (xe ? (zh ? '展覽' : 'Exhibition') : ''))
  const titleZh = pick(true, entry.titleZh) || (asWork ? w.titleZh || w.title : x ? x.titleZh || x.title : extra?.titleZh)
  const titleEn = pick(false, null, entry.titleEn) || (asWork ? w.title : x ? x.title : extra?.titleEn)
  const title = zh ? titleZh || titleEn : titleEn || titleZh
  const year = asWork ? w.year : x ? x.year : extra?.year
  // 中文頁的副標放英文名（不同時才放）；英文頁只放年份
  const subtitle = zh ? [titleEn && titleEn !== titleZh ? titleEn : '', year].filter(Boolean).join(' · ') : String(year || '')
  const desc = pick(zh, entry.descZh, entry.descEn) || (asWork ? pick(zh, w.descriptionZh, w.description) : x ? pick(zh, x.descriptionZh, x.description) : pick(zh, extra?.descZh, extra?.descEn))
  const fallbackExhImage = x ? `media:work/exh-${x.slug}/cover.webp` : ''
  const image = resolveMedia(entry.image || '') || (asWork ? resolveMedia(w.cover) : '') || resolveMedia(fallbackExhImage) || (x ? resolveMedia(x.cover) : '') || resolveMedia(extra?.image || '')
  const facts = []
  for (const f of entry.facts || extra?.facts || []) facts.push({ label: pick(zh, f.labelZh, f.labelEn), value: pick(zh, f.valueZh, f.valueEn) })
  const shown = x || xe
  if (shown && !(entry.facts || extra?.facts)?.length) {
    const d = dateRange(shown.dates)
    if (d) facts.push({ label: zh ? '展期' : 'Dates', value: d })
    const venue = x ? (pick(zh, x.venue?.zh, x.venue?.en) || pick(zh, x.locationZh, x.location)) : [pick(zh, xe.venueZh, xe.venueEn), pick(zh, xe.cityZh, xe.cityEn)].filter(Boolean).join(zh ? '，' : ', ')
    if (venue) facts.push({ label: zh ? '場地' : 'Venue', value: venue })
  }
  const links = []
  if (asWork) links.push({ label: zh ? '進入作品' : 'View work', to: `/work/${w.slug}/` })
  if (x) links.push({ label: zh ? '展覽紀錄' : 'Exhibition record', to: exhibitionPath(x) })
  if (w && entry.workAsLink) links.push({ label: zh ? `作品：${w.titleZh || w.title}` : `Work: ${w.title}`, to: `/work/${w.slug}/` })
  for (const l of extra?.links || []) links.push({ label: pick(zh, l.labelZh, l.labelEn), to: l.to, href: l.href })
  // 作品＋展覽合成一筆時，側欄放一個展覽盒（場地、展期、現場紀錄張數）
  const exhBox = combined ? (x ? {
    venue: pick(zh, x.venue?.zh, x.venue?.en) || pick(zh, x.locationZh, x.location),
    dates: dateRange(x.dates),
    photos: (x.imageDetails || []).length,
    to: exhibitionPath(x),
  } : { venue: [pick(zh, xe.venueZh, xe.venueEn), pick(zh, xe.cityZh, xe.cityEn)].filter(Boolean).join(zh ? '，' : ', '), dates: dateRange(xe.dates), photos: 0, label: pick(zh, xe.boxZh, xe.boxEn) }) : null
  const credit = pick(zh, entry.creditZh || extra?.creditZh, entry.creditEn || extra?.creditEn)
  // 點大圖去的地方：站內的第一個連結（作品頁、展覽頁），沒有才用站外的
  const primary = links.find(l => l.to) || links.find(l => l.href) || null
  return {
    key: [entry.work, entry.exhibition, entry.extra].filter(Boolean).join('+'),
    slug: asWork ? w.slug : '',   // 基因條用（2026-10-04）：只有「作品」那一筆帶，展覽與連到作品的展覽不帶
    kind, title, subtitle, year, desc, image, facts, links, primary, exhBox, credit,
    isWork: !!asWork, isExhibition: !!x || extra?.kind === 'exhibition',
    threads: (entry.threads || []).map(id => threadById[id]).filter(Boolean),
    alt: title,
  }
}

// 一年裡的每一筆（C 版式展開成多件），給篩選、清單與測試用
export const entryItems = entry => (entry.layout === 'C' ? entry.items : [entry])

// 這一頁列出的所有參照：主頁要求每一件作品、每一場展覽都在頁面上（哲宇 2026-10-03）
export function allRefs() {
  const refs = []
  for (const y of showcase.years) for (const e of y.entries) for (const it of entryItems(e)) refs.push(it)
  if (showcase.series?.work) refs.push({ work: showcase.series.work })
  for (const c of showcase.commissions?.featured || []) refs.push(c)
  return refs
}

export default showcase
